import * as React from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./dialog"
import { Button } from "./button"
import { Input } from "./input"
import { Label } from "./label"

// ============================================================================
// InputDialog Component
// ============================================================================

interface InputDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  label?: string
  placeholder?: string
  defaultValue?: string
  confirmText?: string
  cancelText?: string
  onConfirm: (value: string) => void
  onCancel?: () => void
}

export function InputDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  defaultValue = "",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}: InputDialogProps) {
  const [value, setValue] = React.useState(defaultValue)

  React.useEffect(() => {
    if (open) {
      setValue(defaultValue)
    }
  }, [open, defaultValue])

  const handleConfirm = () => {
    onConfirm(value)
    onOpenChange(false)
  }

  const handleCancel = () => {
    onCancel?.()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {label && <Label htmlFor="input-dialog-field">{label}</Label>}
          <Input
            id="input-dialog-field"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleConfirm()
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button onClick={handleConfirm}>{confirmText}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// usePrompt Hook - Imperative prompt dialog
// ============================================================================

interface PromptOptions {
  title: string
  description?: string
  label?: string
  placeholder?: string
  defaultValue?: string
  confirmText?: string
  cancelText?: string
}

interface PromptState extends PromptOptions {
  isOpen: boolean
  resolve: ((value: string | null) => void) | null
}

const PromptContext = React.createContext<{
  prompt: (options: PromptOptions) => Promise<string | null>
} | null>(null)

export function PromptProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<PromptState>({
    isOpen: false,
    title: "",
    resolve: null,
  })

  const prompt = React.useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        ...options,
        resolve,
      })
    })
  }, [])

  const handleConfirm = React.useCallback((value: string) => {
    state.resolve?.(value)
    setState((prev) => ({ ...prev, isOpen: false, resolve: null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state.resolve is the only part of state needed
  }, [state.resolve])

  const handleCancel = React.useCallback(() => {
    state.resolve?.(null)
    setState((prev) => ({ ...prev, isOpen: false, resolve: null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state.resolve is the only part of state needed
  }, [state.resolve])

  return (
    <PromptContext.Provider value={{ prompt }}>
      {children}
      <InputDialog
        open={state.isOpen}
        onOpenChange={(open) => !open && handleCancel()}
        title={state.title}
        description={state.description}
        label={state.label}
        placeholder={state.placeholder}
        defaultValue={state.defaultValue}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </PromptContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePrompt() {
  const context = React.useContext(PromptContext)
  if (!context) {
    throw new Error("usePrompt must be used within a PromptProvider")
  }
  return context.prompt
}






